import { Box, InputBase, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { ReactNode } from 'react';

export interface PmFilterDef {
  key: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
}

interface PmToolbarProps {
  search?: { value: string; placeholder?: string; onChange: (s: string) => void };
  filters?: PmFilterDef[];
  onFilterChange?: (key: string, value: string) => void;
  /** Rechtsbuendige Aktionen, z. B. "+ Neu"-Button. */
  actions?: ReactNode;
}

/** PM-Toolbar — Mini-Toolbar mit Suche + Filter-Dropdowns (Clickdummy `.fbar`). */
export function PmToolbar({ search, filters, onFilterChange, actions }: PmToolbarProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        padding: '8px 12px',
        background: 'var(--pm-bg-subtle)',
        border: '1px solid var(--pm-bg-deep)',
        borderRadius: 'var(--pm-radius)',
      }}
    >
      {search && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flex: '1 1 240px',
            minWidth: 200,
            background: 'var(--pm-bg-deep)',
            borderRadius: '8px',
            padding: '4px 10px',
            color: 'var(--pm-fg-subtle)',
          }}
        >
          <SearchIcon fontSize="small" />
          <InputBase
            value={search.value}
            placeholder={search.placeholder ?? 'Suchen ...'}
            onChange={(e) => search.onChange(e.target.value)}
            sx={{ color: 'var(--pm-fg-base)', fontSize: '0.85rem', flex: 1 }}
          />
        </Box>
      )}
      {filters?.map((f) => (
        <Select
          key={f.key}
          value={f.value}
          size="small"
          variant="outlined"
          onChange={(e: SelectChangeEvent<string>) => onFilterChange?.(f.key, e.target.value)}
          sx={{
            background: 'var(--pm-bg-deep)',
            color: 'var(--pm-fg-base)',
            fontSize: '0.85rem',
            minWidth: 160,
            '.MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pm-bg-muted)' },
          }}
        >
          {f.options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      ))}
      {actions && <Box sx={{ marginLeft: 'auto' }}>{actions}</Box>}
    </Box>
  );
}
